import { KeyboardArrowDown } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { MuiMultiFieldWithFocus } from '../../../../config/MaterialUIConstants';
import { APPOINTMENT_CONTACT_PREFRENCES } from '../../../../utility/Constant';
import PaymentDetailSvg from '../../../../utility/Svg/PaymentDetailSvg';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    '& .MuiToggleButtonGroup-grouped': {
        margin: 0,
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '8px',
        '&:not(:first-of-type)': {
            marginLeft: 0,
            borderRadius: '8px',
        },
        '&:first-of-type': {
            borderRadius: '8px',
        },
    },
}));

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
    padding: '8px 16px',
    fontSize: '16px',
    height: '40px',
    fontWeight: 400,
    lineHeight: '150%',
    letterSpacing: '0.15px',
    textTransform: 'none',
    color: '#000',
    backgroundColor: '#fff',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '100px!important',
    '&.Mui-selected': {
        backgroundColor: '#007AFF',
        color: '#fff',
        border: '1px solid #0066E1',
        '&:hover': {
            backgroundColor: '#0066E1',
        },
    },
    '&:not(.Mui-selected)': {
        backgroundColor: '#fff',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        border: '1px solid #DCDCDC',
        '&:hover': {
            backgroundColor: '#fff',
        },
    },
}));

const AdditionalDetailsAccordion = ({ control, errors, customerOnSite, setCustomerOnSite }) => {
    const [accordionExpanded, setAccordionExpanded] = useState(true);

    const handleAccordionToggle = () => {
        setAccordionExpanded(!accordionExpanded);
    };

    return (
        <Grid item xs={12}>
            <Accordion
                expanded={accordionExpanded}
                style={{
                    boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.10)',
                    borderRadius: '8px',
                    background: '#fff',
                }}
            >
                <AccordionSummary
                    expandIcon={
                        <IconButton style={{ background: '#334FA9', padding: '0', width: '30px', height: '30px' }}>
                            <KeyboardArrowDown sx={{ color: '#fff', fontSize: '26px' }} />
                        </IconButton>
                    }
                    aria-controls="panel2-content"
                    id="panel2-header"
                    className="accordianSummaryPadding"
                    style={{
                        minHeight: '80px',
                    }}
                    onClick={handleAccordionToggle}
                >
                    <div className="accordianTitleWrapper">
                        <PaymentDetailSvg />
                        <Typography className="accordianTitle">Additional Details</Typography>
                    </div>
                </AccordionSummary>
                <AccordionDetails className="accordianDetailsWrapper">
                    <Grid container spacing={3}>
                        {/* Is the Customer Waiting on Site? field */}
                        <Grid item xs={12}>
                            <FormControl fullWidth required error={!!errors?.isWaitonSite}>
                                <Typography variant="body1" className="formLabelTitle" sx={{ mb: 1 }}>
                                    Is the Customer Waiting on Site?
                                </Typography>
                                <Controller
                                    name="isWaitonSite"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <StyledToggleButtonGroup
                                            value={value}
                                            exclusive
                                            onChange={(event, newValue) => {
                                                if (newValue !== null) {
                                                    onChange(newValue);
                                                    setCustomerOnSite(newValue);
                                                }
                                            }}
                                            aria-label="customer waiting on site"
                                            sx={{ gap: 1.5 }}
                                        >
                                            <StyledToggleButton value="wait_on_site" aria-label="wait on site">
                                                Wait on Site
                                            </StyledToggleButton>
                                            <StyledToggleButton value="dropping_off_car" aria-label="dropping off car">
                                                Dropping off Car
                                            </StyledToggleButton>
                                        </StyledToggleButtonGroup>
                                    )}
                                />
                                {errors.isWaitonSite && (
                                    <FormHelperText sx={{ mt: 1 }}>{errors?.isWaitingOnSite?.message || 'This field is required.'}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>

                        {/* Contact Preference field */}
                        <Grid item xs={12}>
                            <FormControl fullWidth error={!!errors.contactPrefrence}>
                                <Stack spacing={1}>
                                    <Typography
                                        variant="body1"
                                        style={{
                                            fontWeight: 500,
                                            fontSize: '16px',
                                            lineHeight: '24px',
                                            letterSpacing: '0.15px',
                                        }}
                                    >
                                        Contact Preference
                                    </Typography>
                                    <Controller
                                        name="contactPrefrence"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <StyledToggleButtonGroup
                                                value={value}
                                                exclusive
                                                onChange={(event, newValue) => {
                                                    if (newValue !== null) {
                                                        onChange(newValue);
                                                    }
                                                }}
                                                aria-label="contact preference"
                                                sx={{ gap: 1.5 }}
                                            >
                                                {APPOINTMENT_CONTACT_PREFRENCES?.map((contact) => (
                                                    <StyledToggleButton key={contact.key} value={contact.key} aria-label={contact.value}>
                                                        {contact.value}
                                                    </StyledToggleButton>
                                                ))}
                                            </StyledToggleButtonGroup>
                                        )}
                                    />
                                </Stack>
                            </FormControl>
                        </Grid>

                        {/* Internal Shop Memo field */}
                        <Grid item xs={12}>
                            <Stack spacing={1}>
                                <Typography variant="body1" className="formLabelTitle" marginBottom={'8px'}>
                                    Internal Shop Memo
                                </Typography>
                                <Controller
                                    name="noteForCustomer"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            multiline
                                            rows={2}
                                            variant="outlined"
                                            error={!!errors.noteForCustomer}
                                            helperText={errors.noteForCustomer ? errors.noteForCustomer.message : ''}
                                            placeholder="Add internal notes..."
                                            sx={MuiMultiFieldWithFocus}
                                        />
                                    )}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Grid>
    );
};

export default AdditionalDetailsAccordion;
